import React from "react"
import { cardRecipe } from "../styles/recipes/card"
import { buttonRecipe } from "../styles/recipes/button"
import { tokens } from "../styles/tokens"
import { Failure } from "../utils/failure"

/**
 * Root render-crash boundary, mounted once in `wrapRootElement`
 * (gatsby-browser.js and gatsby-ssr.js) around the whole app. A class
 * component because `getDerivedStateFromError`/`componentDidCatch` have no
 * hook equivalent.
 *
 * Deliberately self-contained: it renders with no dependency on
 * `StoreLayout`/`PageBody`/site metadata, since the crash it's catching may
 * have come from inside one of those.
 *
 * @see AppErrorBoundary.test.jsx
 */
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error) {
    // The terminal handler for a render crash — logs the Failure once here.
    console.error(
      new Failure(
        "The page ran into a problem and could not continue.",
        "Reload the page. If this keeps happening, contact support.",
        { cause: error, source: "React" }
      )
    )
  }

  handleReload = () => {
    if (typeof window !== "undefined") window.location.reload()
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <div className={tokens.containers.fullscreenCentered}>
        <div className={cardRecipe({ variant: "shell" })}>
          <div
            className={tokens.spacing.stack.sm}
            role="alert"
            aria-live="assertive"
          >
            <h1 className={tokens.text.heading.h1}>
              Something went wrong
            </h1>
            <p className={tokens.text.body.default}>
              This page ran into a problem and could not continue.
            </p>
            <button
              type="button"
              onClick={this.handleReload}
              className={buttonRecipe({ variant: "primary" })}
            >
              Reload the page
            </button>
          </div>
        </div>
      </div>
    )
  }
}

export default AppErrorBoundary
